import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { GraduationCap, BookOpen, ChevronRight, CheckCircle2, Circle, Lock,
          Flame, Trophy, ArrowLeft, LogOut, Sparkles, Loader2 } from "lucide-react";
import SandboxTask from "./academy/SandboxTask";
import SimulatorChat from "./academy/SimulatorChat";
import LessonVideo from "../components/LessonVideo";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Angiolini Academy — trainee-facing LMS.
 *
 * Root of `/academy` renders the "map" (modules + lessons). Deep-linking
 * to `/academy/lesson/:id` opens the reader with markdown + test.
 * All API calls send credentials so the httpOnly-JWT cookie flows.
 */
export default function AcademyRoot() {
  return (
    <div style={{ minHeight: "100vh", background: "#faf9f5",
                   fontFamily: "Manrope, sans-serif" }}>
      <TopBar />
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 20px 60px" }}>
        <AcademyMap />
      </div>
    </div>
  );
}

export function AcademyLessonRoot() {
  const { lessonId } = useParams();
  return (
    <div style={{ minHeight: "100vh", background: "white",
                   fontFamily: "Manrope, sans-serif" }}>
      <TopBar />
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 20px 60px" }}>
        <LessonReader lessonId={lessonId} />
      </div>
    </div>
  );
}

function TopBar() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setMe(r.data)).catch(() => {});
  }, []);
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
    navigate("/auth");
  };
  return (
    <div style={{ background: "#0A0A0A", color: "#D4B878",
                   padding: "12px 24px",
                   display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Link to="/academy" style={{ display: "flex", alignItems: "center", gap: 8,
                                       color: "#D4B878", textDecoration: "none" }}>
        <GraduationCap size={22} />
        <span style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
          Angiolini Academy
        </span>
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {me && (
          <span style={{ fontSize: 12, color: "#a8935f" }}>
            {me.name || me.email}
          </span>
        )}
        <button data-testid="academy-logout" onClick={logout}
          style={{ background: "transparent", border: "1px solid #C5A059",
                    color: "#D4B878", borderRadius: 6, padding: "6px 12px",
                    cursor: "pointer", fontSize: 12, display: "inline-flex",
                    alignItems: "center", gap: 6 }}>
          <LogOut size={12} /> Выйти
        </button>
      </div>
    </div>
  );
}

/* --------------------------- MAP --------------------------- */

function AcademyMap() {
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [lessonsByModule, setLessonsByModule] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Менеджеры и руководители заходят повторять материал, а не «расти до менеджера».
  const isStaffLearner = user?.role && user.role !== "trainee";

  useEffect(() => {
    axios.get(`${API}/academy/modules`, { withCredentials: true })
      .then(async r => {
        const mods = r.data.modules || [];
        setModules(mods);
        // Load lessons per module in parallel.
        const lessonPairs = await Promise.all(mods.map(m =>
          axios.get(`${API}/academy/modules/${m.id}/lessons`, { withCredentials: true })
            .then(x => [m.id, x.data.lessons || []])
            .catch(() => [m.id, []])
        ));
        const byMod = {};
        lessonPairs.forEach(([mid, arr]) => { byMod[mid] = arr; });
        setLessonsByModule(byMod);
      })
      .catch(() => {});
    axios.get(`${API}/academy/progress`, { withCredentials: true })
      .then(r => setProgress(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openLesson = (id) => navigate(`/academy/lesson/${id}`);

  const nextLesson = useMemo(() => {
    for (const m of modules) {
      const lessons = lessonsByModule[m.id] || [];
      for (const l of lessons) {
        if (l.is_published && !l.completed && !l.is_locked) return l;
      }
    }
    return null;
  }, [modules, lessonsByModule]);

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
    <Loader2 size={24} className="animate-spin" style={{ display: "inline" }} />
  </div>;

  return (
    <>
      {/* Hero */}
      <div style={{ background: "white", borderRadius: 12, padding: 28,
                     marginBottom: 20, border: "1px solid #efeee9",
                     boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 30, color: "#0A0A0A",
                       marginBottom: 6 }}>
          Добро пожаловать в Angiolini Academy
        </div>
        <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>
          {isStaffLearner
            ? "Повторяйте темы в любом порядке, проходите тесты и держите знания в форме"
            : "Ваш путь до ONLINE-менеджера"}
        </div>

        {progress && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151",
                              textTransform: "uppercase", letterSpacing: 1 }}>
                Прогресс: {progress.percent_complete}%
              </span>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                Пройдено уроков: {progress.lessons_completed} / {progress.lessons_total}
              </span>
            </div>
            <div style={{ background: "#f3f4f6", borderRadius: 4, height: 8, overflow: "hidden" }}>
              <div data-testid="academy-progress-bar"
                style={{ background: "linear-gradient(90deg,#C5A059,#D4B878)",
                         width: `${progress.percent_complete}%`, height: "100%",
                         transition: "width 0.3s" }} />
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
              <StatChip icon={Sparkles} label="XP" value={progress.xp} />
              <StatChip icon={Trophy} label="Уровень" value={progress.level_label} />
              {progress.streak_days > 0 && (
                <StatChip icon={Flame} label="Дней подряд"
                  value={`🔥 ${progress.streak_days}${progress.streak_active_today ? "" : " (сегодня ещё нет)"}`}  />
              )}
            </div>
            {progress.achievements && progress.achievements.length > 0 && (
              <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {progress.achievements.map(a => (
                  <div key={a.key} data-testid={`achievement-${a.key}`}
                    title={a.label}
                    style={{ background: "rgba(212,184,120,0.15)",
                              border: "1px solid #C5A059",
                              borderRadius: 20, padding: "4px 12px",
                              fontSize: 12, color: "#0A0A0A",
                              display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 14 }}>{a.icon}</span> {a.label}
                  </div>
                ))}
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)",
                                alignSelf: "center" }}>
                  {progress.achievements.length}/{progress.achievements_total}
                </div>
              </div>
            )}
          </div>
        )}

        {nextLesson && (
          <button data-testid="academy-continue-btn"
            onClick={() => openLesson(nextLesson.id)}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", border: "none", borderRadius: 8,
                      padding: "14px 24px", fontWeight: 800, fontSize: 13,
                      letterSpacing: 0.5, textTransform: "uppercase",
                      cursor: "pointer", display: "inline-flex",
                      alignItems: "center", gap: 8 }}>
            Продолжить обучение <ChevronRight size={16} />
          </button>
        )}
      </div>

      <FinalExamCard progress={progress} />

      {/* Modules */}
      {modules.map((m) => (
        <div key={m.id} data-testid={`academy-module-${m.slug}`}
          style={{ background: "white", borderRadius: 12, marginBottom: 14,
                    border: "1px solid #efeee9",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.03)", overflow: "hidden" }}>
          <div style={{ padding: "18px 22px", borderBottom: "1px solid #f5f4ef" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#0A0A0A" }}>
                  {m.title}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  {m.description}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {m.slug === "crm-angiolini" && (
                  <button data-testid="academy-reset-sandbox"
                    onClick={async () => {
                      if (!window.confirm("Сбросить песочницу? Ваши учебные клиенты, товары и подборки будут удалены.")) return;
                      try {
                        await axios.post(`${API}/academy/sandbox/reset`, {}, { withCredentials: true });
                        alert("Песочница сброшена ✓");
                      } catch {
                        alert("Не удалось сбросить");
                      }
                    }}
                    style={{ fontSize: 11, background: "transparent",
                              border: "1px solid #d1d5db", borderRadius: 6,
                              padding: "4px 10px", cursor: "pointer",
                              color: "#64748b" }}>
                    Сбросить песочницу
                  </button>
                )}
                <div style={{ fontSize: 11, color: "#8a8a89",
                               fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                  {m.lessons_completed} / {m.lessons_count} готово
                </div>
              </div>
            </div>
          </div>
          <div>
            {(lessonsByModule[m.id] || []).map((l) => (
              <LessonRow key={l.id} lesson={l}
                onClick={() => openLesson(l.id)} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 10, color: "#8a8a89", textTransform: "uppercase",
                     letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 3 }}>
        <Icon size={12} /> {label}
      </div>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20,
                     fontWeight: 700, color: "#0A0A0A" }}>
        {value}
      </div>
    </div>
  );
}

function LessonRow({ lesson, onClick }) {
  const locked = !!lesson.is_locked;
  return (
    <button data-testid={`academy-lesson-${lesson.slug}`}
      onClick={locked ? undefined : onClick}
      disabled={locked}
      title={locked ? "Пройдите предыдущий урок, чтобы открыть этот" : undefined}
      style={{ display: "flex", alignItems: "center", gap: 12,
                width: "100%", padding: "14px 22px",
                background: "transparent", border: "none",
                cursor: locked ? "not-allowed" : "pointer",
                borderTop: "1px solid #fafaf9", textAlign: "left",
                opacity: locked ? 0.5 : 1 }}>
      {locked
        ? <Lock size={16} color="#9ca3af" />
        : lesson.completed
          ? <CheckCircle2 size={16} color="#059669" />
          : <Circle size={16} color="#d1d5db" />}
      <span style={{ flex: 1, fontFamily: "Manrope", fontSize: 14,
                     color: locked ? "#6b7280" : "#0A0A0A" }}>
        {lesson.title}
      </span>
      {locked && (
        <span data-testid={`academy-lesson-${lesson.slug}-locked-badge`}
          style={{ fontSize: 10, fontWeight: 700, color: "#6b7280",
                    border: "1px solid #d1d5db", borderRadius: 4,
                    padding: "2px 6px", textTransform: "uppercase",
                    letterSpacing: 0.5 }}>
          Закрыто
        </span>
      )}
      {!locked && lesson.practical_task && (
        <span style={{ fontSize: 10, fontWeight: 700, color: "#C5A059",
                        border: "1px solid #C5A059", borderRadius: 4,
                        padding: "2px 6px", textTransform: "uppercase",
                        letterSpacing: 0.5 }}>
          Практика
        </span>
      )}
      {!locked && lesson.simulator_scenario && (
        <span style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed",
                        border: "1px solid #a78bfa", borderRadius: 4,
                        padding: "2px 6px", textTransform: "uppercase",
                        letterSpacing: 0.5,
                        background: "rgba(167,139,250,0.08)" }}>
          AI-клиент
        </span>
      )}
      {!locked && lesson.best_score > 0 && (
        <span style={{ fontSize: 11, color: lesson.completed ? "#059669" : "#B45309", fontWeight: 700 }}>
          {lesson.best_score}%
        </span>
      )}
      <ChevronRight size={16} color="#c0c0c0" />
    </button>
  );
}

/* --------------------------- LESSON READER --------------------------- */

const MD_COMPONENTS = {
  img: ({ node, ...props }) => (
    <img {...props} alt={props.alt || ""}
      style={{ maxWidth: "100%", borderRadius: 10, margin: "14px 0",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }} />
  ),
};

function LessonReader({ lessonId }) {
  const [lesson, setLesson] = useState(null);
  const [phase, setPhase] = useState("read"); // read | test | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [watch, setWatch] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/academy/lessons/${lessonId}`, { withCredentials: true })
      .then(r => setLesson(r.data))
      .catch(() => setLesson({ error: "not-found" }));
  }, [lessonId]);

  if (!lesson) return <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
    <Loader2 size={24} className="animate-spin" style={{ display: "inline" }} />
  </div>;
  if (lesson.error) return <div style={{ padding: 40, textAlign: "center" }}>
    Урок не найден. <Link to="/academy">К списку</Link>
  </div>;

  const watchPercent = watch != null ? watch : (lesson.progress?.watch_percent || 0);
  const videoLocked = !!lesson.video_url
    && watchPercent < (lesson.video_required_percent || 80);

  const submitTest = async () => {
    // Client-side guard: refuse to submit until every question has an
    // answer selected. Without this, forgotten questions get auto-graded
    // as wrong and trainees think the system is buggy.
    const unanswered = (lesson.test || []).filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      const nums = unanswered.map(q => (lesson.test.findIndex(x => x.id === q.id) + 1)).join(", ");
      alert(`Ответьте на все вопросы. Пропущены: ${nums}.`);
      // Scroll to the first missing question so the trainee can find it.
      const first = document.querySelector(`[data-testid="academy-q-${unanswered[0].id}"]`);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/academy/lessons/${lessonId}/test`,
        { answers }, { withCredentials: true });
      setResult(r.data);
      setPhase("result");
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка проверки");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button data-testid="academy-back-btn"
        onClick={() => navigate("/academy")}
        style={{ background: "transparent", border: "none", color: "#64748b",
                  cursor: "pointer", display: "inline-flex", alignItems: "center",
                  gap: 6, fontSize: 13, marginBottom: 12 }}>
        <ArrowLeft size={14} /> К списку уроков
      </button>

      {phase === "read" && (
        <>
          {lesson.video_url && (
            <div style={{ marginBottom: 20 }}>
              <LessonVideo lessonId={lessonId} videoUrl={lesson.video_url}
                poster={lesson.video_poster}
                requiredPercent={lesson.video_required_percent}
                startPercent={lesson.progress?.watch_percent || 0}
                onProgress={(d) => setWatch(d.watch_percent)} />
            </div>
          )}
          <div className="academy-md" style={{ lineHeight: 1.7, color: "#0A0A0A" }}>
            <ReactMarkdown components={MD_COMPONENTS}>
              {lesson.content_md}
            </ReactMarkdown>
          </div>

          {lesson.practical_task && (
            <SandboxTask taskId={lesson.practical_task}
              onCompleted={() => {
                // Refresh lesson so the "completed" flag updates.
                axios.get(`${API}/academy/lessons/${lessonId}`, { withCredentials: true })
                  .then(r => setLesson(r.data)).catch(() => {});
              }} />
          )}

          {lesson.simulator_scenario && (
            <SimulatorChat scenarioId={lesson.simulator_scenario}
              onCompleted={() => {
                axios.get(`${API}/academy/lessons/${lessonId}`, { withCredentials: true })
                  .then(r => setLesson(r.data)).catch(() => {});
              }} />
          )}

          {!lesson.practical_task && lesson.test && lesson.test.length > 0 && (
            <div style={{ marginTop: 32, padding: 22, background: "#faf9f5",
                           borderRadius: 10, border: "1px solid #efeee9" }}>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20, marginBottom: 6 }}>
                Проверьте себя
              </div>
              <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 14 }}>
                {lesson.test.length} вопрос(а). Проходной балл: {lesson.min_test_score}%.
                {lesson.progress?.best_score
                  ? ` Ваш лучший результат: ${lesson.progress.best_score}%.`
                  : ""}
              </div>
              {videoLocked ? (
                <div data-testid="academy-test-locked"
                  style={{ fontSize: 12.5, color: "#B45309", fontWeight: 600 }}>
                  Тест откроется после просмотра видео на {lesson.video_required_percent}%
                  (сейчас {watchPercent}%)
                </div>
              ) : (
                <button data-testid="academy-start-test"
                  onClick={() => setPhase("test")}
                  style={{ background: "#0A0A0A", color: "#D4B878", border: "1px solid #C5A059",
                            borderRadius: 6, padding: "10px 20px", fontWeight: 700,
                            fontSize: 13, cursor: "pointer" }}>
                  Пройти тест
                </button>
              )}
            </div>
          )}
        </>
      )}

      {phase === "test" && (
        <>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 26, marginBottom: 16 }}>
            Тест: {lesson.title}
          </div>
          {lesson.test.map((q, qi) => (
            <div key={q.id} data-testid={`academy-q-${q.id}`}
              style={{ marginBottom: 20, padding: 18, background: "white",
                        border: "1px solid #efeee9", borderRadius: 8 }}>
              {q.image && (
                <img src={q.image.startsWith("http") ? q.image : (process.env.REACT_APP_BACKEND_URL + q.image)}
                  alt="" data-testid={`academy-q-${q.id}-image`}
                  style={{ maxWidth: "100%", maxHeight: 340,
                            borderRadius: 8, marginBottom: 14,
                            display: "block", objectFit: "contain",
                            background: "#faf9f5" }} />
              )}
              <div style={{ fontFamily: "Manrope", fontSize: 14, fontWeight: 700,
                             color: "#0A0A0A", marginBottom: 12 }}>
                {qi + 1}. {q.question}
              </div>
              {q.options.map((opt, oi) => (
                <label key={oi}
                  style={{ display: "flex", gap: 10, padding: "10px 12px",
                            marginBottom: 5, borderRadius: 6, cursor: "pointer",
                            border: answers[q.id] === oi ? "1px solid #C5A059" : "1px solid #f1f1ee",
                            background: answers[q.id] === oi ? "rgba(197,160,89,0.05)" : "white" }}>
                  <input type="radio" name={q.id} checked={answers[q.id] === oi}
                    onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                    data-testid={`academy-q-${q.id}-opt-${oi}`} />
                  <span style={{ fontSize: 13, color: "#0A0A0A" }}>{opt}</span>
                </label>
              ))}
            </div>
          ))}
          <button data-testid="academy-submit-test"
            onClick={submitTest} disabled={submitting}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                      color: "#0A0A0A", border: "none", borderRadius: 8,
                      padding: "12px 28px", fontWeight: 800,
                      textTransform: "uppercase", letterSpacing: 0.5, fontSize: 13,
                      cursor: submitting ? "wait" : "pointer" }}>
            {submitting ? "Проверяем..." : "Проверить"}
          </button>
        </>
      )}

      {phase === "result" && result && (
        <div style={{ padding: 24, borderRadius: 12,
                       background: result.passed ? "#ecfdf5" : "#fef2f2",
                       border: "1px solid " + (result.passed ? "#a7f3d0" : "#fecaca") }}>
          <div style={{ fontSize: 48, marginBottom: 4 }}>
            {result.passed ? "🎉" : "📚"}
          </div>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 32,
                         color: result.passed ? "#065f46" : "#7f1d1d" }}>
            {result.score}% · {result.correct} из {result.total}
          </div>
          <div style={{ fontSize: 14, color: "#374151", marginTop: 6 }}>
            {result.passed
              ? `Отлично! Тест пройден. ${result.xp_earned ? "+" + result.xp_earned + " XP" : ""}`
              : `Пока не хватает — проходной балл ${result.min_pass}%. Повторите материал и попробуйте снова.`}
          </div>
          {/* Show explanations for wrong answers */}
          <div style={{ marginTop: 20 }}>
            {result.results.filter(r => !r.is_correct).map((r) => {
              const q = lesson.test.find(x => x.id === r.id);
              const pickedLabel = (r.picked === null || r.picked === undefined)
                ? "— (вопрос пропущен)"
                : (q?.options?.[r.picked] || "—");
              return (
                <div key={r.id} style={{ marginBottom: 12, padding: 12,
                                            background: "white", borderRadius: 6,
                                            border: "1px solid #fee2e2" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    {q?.question}
                  </div>
                  <div data-testid={`academy-result-picked-${r.id}`}
                    style={{ fontSize: 12, color: "#b91c1c", marginBottom: 3 }}>
                    Вы выбрали: {pickedLabel}
                  </div>
                  <div style={{ fontSize: 12, color: "#059669" }}>
                    Правильный ответ: {q?.options[r.correct_index]}
                  </div>
                  {r.explanation && (
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                      {r.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button data-testid="academy-retry-test"
              onClick={() => { setPhase("test"); setResult(null); setAnswers({}); }}
              style={{ background: "white", border: "1px solid #d1d5db",
                        borderRadius: 6, padding: "10px 20px",
                        fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
              Пройти снова
            </button>
            <button data-testid="academy-next-lesson"
              onClick={() => navigate("/academy")}
              style={{ background: "#0A0A0A", color: "#D4B878", border: "none",
                        borderRadius: 6, padding: "10px 20px",
                        fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              К списку уроков
            </button>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * Sticky card between the Hero and Modules that surfaces the final exam
 * once the trainee crosses 100% completion. Also shows result summary
 * after a completed attempt + a "Request promotion" CTA.
 */
function FinalExamCard({ progress }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Заявка на перевод — только для стажёров; менеджеры сдают экзамен
  // для самопроверки и повторения.
  const isTrainee = user?.role === "trainee";
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [reqLoading, setReqLoading] = React.useState(false);

  React.useEffect(() => {
    if (!progress || progress.percent_complete < 100) return;
    setLoading(true);
    axios.get(`${API}/academy/final-exam/eligibility`, { withCredentials: true })
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [progress]);

  if (!progress) return null;
  if (progress.percent_complete < 100) {
    return (
      <div data-testid="final-exam-locked"
        style={{ background: "linear-gradient(135deg,#111 0%,#1a1a1a 100%)",
                  border: "1px solid #333", borderRadius: 12, padding: 22,
                  marginBottom: 20, color: "#a1a1aa" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <Lock size={22} color="#a1a1aa" />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#D4B878" }}>
              Финальный экзамен
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Откроется после прохождения 100% программы. Сейчас: {progress.percent_complete}%
            </div>
          </div>
        </div>
      </div>
    );
  }

  const requestPromo = async () => {
    setReqLoading(true);
    try {
      await axios.post(`${API}/academy/final-exam/request-promotion`, {},
        { withCredentials: true });
      alert("Заявка на перевод отправлена руководителю ✓");
      const r = await axios.get(`${API}/academy/final-exam/eligibility`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось отправить заявку");
    } finally { setReqLoading(false); }
  };

  const last = status?.last_attempt;
  const passed = last?.passed;
  const promoSent = last?.promotion_status === "pending";
  const promoApproved = last?.promotion_status === "approved";

  return (
    <div data-testid="final-exam-card"
      style={{ background: "linear-gradient(135deg,#0A0A0A 0%,#1a1a1a 100%)",
                border: "1px solid #C5A059", borderRadius: 12, padding: 24,
                marginBottom: 20, color: "#e5e7eb",
                boxShadow: "0 8px 24px rgba(197,160,89,0.15)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
        <Trophy size={28} color="#D4B878" style={{ flexShrink: 0, marginTop: 4 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 26, color: "#D4B878" }}>
            Финальный экзамен
          </div>
          <div style={{ fontSize: 13, color: "#a1a1aa", marginTop: 4, marginBottom: 14 }}>
            5 частей: Продукт · Продажи · CRM · Возражения · Полная продажа. Проходной балл — 80%.
          </div>

          {loading && <div style={{ fontSize: 12 }}>Загрузка…</div>}

          {last && (
            <div data-testid="final-exam-last-result"
              style={{ background: "rgba(197,160,89,0.08)",
                        border: "1px solid rgba(197,160,89,0.3)",
                        borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#a1a1aa", marginBottom: 4,
                             textTransform: "uppercase", letterSpacing: 1 }}>
                Последний результат
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <span style={{ fontSize: 34, fontWeight: 800,
                                color: passed ? "#22c55e" : "#f97316" }}>
                  {last.aggregate_score ?? "—"}%
                </span>
                <span style={{ fontSize: 13, fontWeight: 700,
                                color: passed ? "#22c55e" : "#f97316",
                                textTransform: "uppercase", letterSpacing: 1 }}>
                  {passed ? "🏆 Пройдено" : "Не пройдено"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8,
                             display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))",
                             gap: 6 }}>
                <div>Продукт: {last.part_scores?.product ?? "—"}%</div>
                <div>Продажи: {last.part_scores?.sales ?? "—"}%</div>
                <div>CRM: {last.part_scores?.crm ?? "—"}%</div>
                <div>Возражения: {last.part_scores?.objections ?? "—"}%</div>
                <div>Практика: {last.part_scores?.full_sale ?? "—"}%</div>
              </div>
            </div>
          )}

          {status?.cooldown_active && (
            <div style={{ fontSize: 12, color: "#f97316", marginBottom: 10 }}>
              Следующая попытка возможна через 24 часа после предыдущей.
            </div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {status?.eligible && (
              <button data-testid="final-exam-start"
                onClick={() => navigate("/academy/final-exam")}
                style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                          color: "#0A0A0A", border: "none", borderRadius: 8,
                          padding: "12px 22px", fontWeight: 800, fontSize: 13,
                          textTransform: "uppercase", letterSpacing: 0.6,
                          cursor: "pointer" }}>
                {last ? "Пересдать" : "Начать экзамен"}
              </button>
            )}
            {passed && !promoApproved && isTrainee && (
              <button data-testid="final-exam-request-promo"
                onClick={requestPromo}
                disabled={reqLoading || promoSent}
                style={{ background: promoSent ? "#4b5563" : "#0A0A0A",
                          color: "#D4B878",
                          border: "1px solid #C5A059", borderRadius: 8,
                          padding: "12px 22px", fontWeight: 700, fontSize: 13,
                          cursor: promoSent ? "default" : "pointer" }}>
                {promoSent ? "Заявка на рассмотрении" : "Отправить заявку на перевод в сотрудники"}
              </button>
            )}
            {promoApproved && (
              <div style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>
                🎉 Вы переведены в менеджеры! Перезайдите в приложение.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
